import { useUserStore } from '@/store/modules/user'
import { getAccessToken, getRefreshToken, getTokenStorageKeys, removeToken } from '@/services/utils/auth'
import { initializeLoginTenantScope, requireLoginTenant } from '@/services/utils/tenantScope'
import { openAuthenticationGateway } from './navigation'
export { openAuthenticationGateway } from './navigation'
import { resolveCollaborationReturnUrl, readCollaborationLoginQuery, isPasswordLoginEntry } from './session'
import { authenticationState, invalidateAuthentication, markAuthenticationReady, registerAuthenticationGuard, resumeAutomaticLogin } from './auth-state'
import { authorizationErrorCode } from './error'

let authentication: Promise<boolean> | undefined
let authenticationGeneration = -1

export function ensureAuthenticated(force = false): Promise<boolean> {
    if (authentication && authenticationGeneration === authenticationState.generation) return authentication
    if (!force && ['ready', 'refreshing'].includes(authenticationState.status)
        && !!authenticationState.token && authenticationState.token === getAccessToken()) {
        return Promise.resolve(true)
    }
    let generation = authenticationState.generation
    authenticationGeneration = generation
    authenticationState.status = 'checking'
    const task = (async () => {
        await initializeLoginTenantScope()
        generation = authenticationState.generation
        authenticationGeneration = generation
        if (!getAccessToken() && !getRefreshToken()) {
            authenticationState.status = 'idle'
            return false
        }
        requireLoginTenant()
        const store = useUserStore()
        try {
            authenticationState.status = 'loading'
            // Bootstrap requests bypass the guard they are responsible for completing.
            const results = await Promise.allSettled([
                store.fetchPrmissionInfo({ authBootstrap: true, silent: true }),
                store.fetchUserInfo({ authBootstrap: true, silent: true })
            ])
            if (generation !== authenticationState.generation) return false
            const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected')
            // A later 401 must not be hidden by an earlier transient network failure.
            if (failures.length) throw (failures.find(result => authorizationErrorCode(result.reason) === 401) || failures[0]).reason
            markAuthenticationReady()
            return true
        } catch (error) {
            if (generation !== authenticationState.generation) return false
            if (authorizationErrorCode(error) === 401) {
                removeToken()
                store.resetState()
                invalidateAuthentication()
                return false
            }
            authenticationState.status = 'error'
            throw error
        }
    })().catch(error => {
        if (generation === authenticationState.generation) authenticationState.status = 'error'
        throw error
    }).finally(() => {
        if (authentication === task) authentication = undefined
    })
    authentication = task
    return task
}

export function installAuthenticationGuard(): void {
    registerAuthenticationGuard(() => ensureAuthenticated())
    window.addEventListener?.('storage', event => {
        if (event.key !== null && !getTokenStorageKeys().includes(event.key)) return
        invalidateAuthentication()
        if (!isAuthenticationPage(location.href)) {
            void openAuthenticationGateway().catch(() =>
                uni.showToast({ title: '登录状态已变化，请重新打开', icon: 'none' }))
        }
    })
}

export function isAuthenticationPage(href: string): boolean {
    const path = new URL(href).hash.split('?')[0]
    // UniApp rewrites the first registered page (the gateway) to the root alias.
    return path === '' || path === '#' || path === '#/' ||
        path.startsWith('#/packageLogin/') || /\/(?:collaboration-binding|social)\/index$/.test(path)
}

/** Preserve the business target before mounting the authentication gateway. */
export function prepareAuthenticationEntry(): void {
    const url = new URL(location.href)
    const path = url.hash.split('?')[0]
    if (isAuthenticationPage(url.href) && path !== '#/packageLogin/pages/login/index') return
    const query = readCollaborationLoginQuery(url.href)
    if (isPasswordLoginEntry(url.href) && path === '#/packageLogin/pages/login/index') return
    const redirect = resolveCollaborationReturnUrl(query.get('redirect') || url.href)
    const tenantId = query.get('tenantId') || url.searchParams.get('tenantId')
    // 新业务入口是新的登录意图；密码页和授权回调不清除用户的主动选择。
    if (!isAuthenticationPage(url.href)) resumeAutomaticLogin(tenantId || undefined)
    const params = new URLSearchParams({ redirect })
    if (tenantId) params.set('tenantId', tenantId)
    if (path === '#/packageLogin/pages/login/index' && query.get('loginIntent') === 'authorize') {
        params.set('loginIntent', 'authorize')
    }
    // 业务链接中的历史禁用参数不能升级成登录意图。
    url.searchParams.delete('noOauth2')
    url.searchParams.delete('loginIntent')
    url.hash = '/packageLogin/pages/session/index?' + params.toString()
    window.history.replaceState(window.history.state, '', url.toString())
}
